import { cx } from "class-variance-authority"
import { createContext, useContext, useEffect, useRef, useState } from "react"

type Context = {
  isOpen: boolean
  isPlaying: boolean
  src: string
  setSource: (src: string) => void
  open: () => void
  close: () => void
  play: () => void
  pause: () => void
}

const Context = createContext<Context>({
  isOpen: false,
  isPlaying: false,
  src: "",
  setSource: () => {},
  open: () => {},
  close: () => {},
  play: () => {},
  pause: () => {},
})

export const Provider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [src, setSrc] = useState("")

  const setSource = (src: string) => {
    setSrc(src)
    setIsOpen(true)
    setIsPlaying(true)
  }

  const open = () => {
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setIsPlaying(false)
  }

  const play = () => {
    setIsPlaying(true)
  }

  const pause = () => {
    setIsPlaying(false)
  }

  return (
    <Context.Provider value={{ isOpen, isPlaying, src, setSource, open, close, play, pause }}>
      {children}
    </Context.Provider>
  )
}

export const useGlobalPlayer = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error("useAudio must be used within an AudioProvider")
  }
  return context
}

export const Player = () => {
  const playerRef = useRef<HTMLAudioElement>(null)
  const { src, isOpen, isPlaying, play, pause } = useGlobalPlayer()

  useEffect(() => {
    if (!playerRef.current) return

    if (isPlaying) {
      playerRef.current.play()
    } else {
      playerRef.current.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    if (!playerRef.current || !src) return

    playerRef.current.play()
  }, [src])

  return (
    <div
      data-hidden={!isOpen}
      className={cx(
        "fixed bottom-0 left-0 w-full z-global-player",
        "p-4 flex items-center justify-center",
        "data-[hidden=true]:translate-y-full transition-transform"
      )}
    >
      <audio
        ref={playerRef}
        controls
        key={src}
        src={src}
        onPlay={() => {
          play()
        }}
        onPause={() => {
          pause()
        }}
      />
    </div>
  )
}
